import { failure, ok, pending, Result } from "@/common/common";
import { DCEvent, UpdateStoreEvent } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import {
  CompanyDashboard,
  CompanyDashboardCard,
  CompanyDashboards,
  DashboardCardData,
  dashboardApiClient,
} from "./dashboardApiClient";
import { textFor } from "./dashboardDialogsDomain";
import { CardCompany, loadCardData } from "./dashboardPreviewDomain";
import { asOfDateModel } from "./dashboardSettingsDomain";
import { LocalDateFieldModel } from "@/common/commonForms";

export type CompanyDashboardsState = {
  orgNo: string;
  dashboards: Result<CompanyDashboards>;
  cardData: Record<string, Result<DashboardCardData>>;
  asOf: LocalDateFieldModel;
};

const updateCompanyDashboards = (
  orgNo: string,
  partial: (state: CompanyDashboardsState) => Partial<CompanyDashboardsState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    companyDashboards: (state) =>
      state?.orgNo === orgNo ? { ...state, ...partial(state) } : state,
  },
});

export const initCompanyDashboards = (orgNo: string): DCEvent[] => [
  {
    type: "update-store",
    data: {
      companyDashboards: (state) =>
        state?.orgNo === orgNo
          ? state
          : { orgNo, dashboards: pending(), cardData: {}, asOf: asOfDateModel() },
    },
  },
  dashboardApiClient.companyDashboards(orgNo, {
    onSuccess: (res) => [updateCompanyDashboards(orgNo, () => ({ dashboards: ok(res.data) }))],
    onFailure: () => [updateCompanyDashboards(orgNo, () => ({ dashboards: failure() }))],
  }),
];

const setCardData = (
  orgNo: string,
  cardId: string,
  result: Result<DashboardCardData>,
): UpdateStoreEvent =>
  updateCompanyDashboards(orgNo, (state) => ({
    cardData: { ...state.cardData, [cardId]: result },
  }));

export const loadCompanyCardData = (
  company: CardCompany,
  cards: CompanyDashboardCard[],
  asOf: string | undefined,
): DCEvent[] =>
  loadCardData(
    company,
    cards.map((card) => ({ key: card.id, config: card.config })),
    { asOf },
    (cardId, result) => setCardData(company.orgNo, cardId, result),
  );

export type CompanyDashboardLink = { id: string; title: string };

export type CompanyDashboardLinks = {
  isSystemDefaultShown: boolean;
  dashboards: CompanyDashboardLink[];
};

const loadedDashboards = (
  state: CompanyDashboardsState | undefined,
  orgNo: string,
): CompanyDashboards | undefined =>
  state?.orgNo === orgNo && state.dashboards.state === "ok" ? state.dashboards.data : undefined;

export const companyDashboardLinks = (
  state: CompanyDashboardsState | undefined,
  orgNo: string,
  language: Language,
): CompanyDashboardLinks => {
  const loaded = loadedDashboards(state, orgNo);
  return {
    isSystemDefaultShown: loaded?.isSystemDefaultEnabled ?? true,
    dashboards: (loaded?.dashboards ?? []).map((dashboard) => ({
      id: dashboard.id,
      title: textFor(dashboard.title, language) ?? "",
    })),
  };
};

export const companyDashboard = (
  state: CompanyDashboardsState | undefined,
  dashboardId: string,
): CompanyDashboard | undefined =>
  state?.dashboards.state === "ok"
    ? state.dashboards.data.dashboards.find((dashboard) => dashboard.id === dashboardId)
    : undefined;

export type SystemDefaultRedirect = { type: "dashboard"; id: string } | { type: "company-tab" };

export const systemDefaultRedirect = (
  state: CompanyDashboardsState | undefined,
  orgNo: string,
): SystemDefaultRedirect | undefined => {
  const loaded = loadedDashboards(state, orgNo);
  if (!loaded || loaded.isSystemDefaultEnabled) return undefined;
  const first = loaded.dashboards[0];
  return first ? { type: "dashboard", id: first.id } : { type: "company-tab" };
};
